import {
  Cancel,
  Close,
  PlayArrow,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname, parseError } from "../../../utils";

interface BrowserSessionDialogProps {
  open: boolean;
  onClose: () => void;
  integration: any;
  companyId: string;
  getToken: () => Promise<string>;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
}

const BrowserSessionDialog: React.FC<BrowserSessionDialogProps> = ({
  open,
  onClose,
  integration,
  companyId,
  getToken,
  onError,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [liveUrl, setLiveUrl] = useState<string | null>(null);
  const [connectUrl, setConnectUrl] = useState<string | null>(null);

  // Task execution state
  const [task, setTask] = useState("");
  const [executing, setExecuting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [history, setHistory] = useState<string[]>([]);
  const [useVision, setUseVision] = useState(false);

  useEffect(() => {
    if (!open) {
      // Reset state when dialog closes
      setSessionId(null);
      setLiveUrl(null);
      setConnectUrl(null);
      setTask("");
      setExecuting(false);
      setCancelling(false);
      setResult(null);
      setHistory([]);
      setUseVision(false);
    }
  }, [open]);

  const handleClose = async () => {
    // Cancel the browser session if one is active
    if (sessionId) {
      try {
        const token = await getToken();
        await axios.post(
          `${hostname}/browser-sessions/cancel/${sessionId}?companyId=${companyId}`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } catch (err) {
        // Log error but don't block closing
        console.error("Error cancelling browser session:", err);
      }
    }
    onClose();
  };

  const handleOpenDebugSession = async () => {
    if (!integration) return;

    setLoading(true);
    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/browser-sessions/integration/${integration.id}/open-debug`,
        { companyId },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSessionId(response.data.sessionId);
      setLiveUrl(response.data.liveUrl);
      setConnectUrl(response.data.connectUrl);
    } catch (err) {
      onError(parseError(err));
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteTask = async () => {
    if (!task.trim() || !integration) return;

    // Check if browser session is open
    if (!sessionId || !connectUrl) {
      onError(
        "Please open the live browser session first before executing tasks"
      );
      return;
    }

    setExecuting(true);
    setResult(null);
    setHistory([]);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/browser-sessions/integration/${integration.id}/execute-task`,
        {
          companyId,
          task: task.trim(),
          sessionId: sessionId,
          connectUrl: connectUrl,
          useVision: useVision,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setResult(response.data);
      setHistory(response.data.history || []);

      if (response.data.success) {
        onSuccess("Task completed successfully");
      }
    } catch (err) {
      onError(parseError(err));
    } finally {
      setExecuting(false);
    }
  };

  const handleCancelTask = async () => {
    if (!sessionId) return;

    setCancelling(true);
    try {
      const token = await getToken();
      // Use the existing cancel endpoint to close the browser session
      // This will cause the running task to fail with a disconnection error
      await axios.post(
        `${hostname}/browser-sessions/cancel/${sessionId}?companyId=${companyId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Clear the session state since the browser is now closed
      setSessionId(null);
      setLiveUrl(null);
      setConnectUrl(null);
      setExecuting(false);
      setResult({
        success: false,
        result: "Task was cancelled and browser session closed",
      });
      onSuccess("Task cancelled successfully");
    } catch (err) {
      onError(parseError(err));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullScreen>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">
            {integration?.type === "linkedin_login" ? "LinkedIn Login" : "Browser Session"} - {integration?.instanceName || integration?.name}
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box
        sx={{
          display: "flex",
          height: "calc(100vh - 64px)",
          overflow: "hidden",
        }}
      >
        {/* Left Panel - Task Execution (30%) */}
        <Box
          sx={{
            width: "30%",
            borderRight: "1px solid #ddd",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 2, borderBottom: "1px solid #ddd" }}>
            <Typography variant="h6" gutterBottom>
              AI Browser Tasks
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {!sessionId
                ? "Open the browser session first, then describe tasks for the AI to perform"
                : "Watch the AI work in the browser on the right →"}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              flex: 1,
              overflow: "auto",
            }}
          >
            <TextField
              label="Task Description"
              placeholder="e.g., Go to LinkedIn and send a connection request to John Doe"
              multiline
              rows={4}
              value={task}
              onChange={(e) => setTask(e.target.value)}
              disabled={executing || !sessionId}
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={useVision}
                  onChange={(e) => setUseVision(e.target.checked)}
                  disabled={executing}
                />
              }
              label={
                <Tooltip title="Enable vision mode for AI to see and analyze page screenshots">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {useVision ? (
                      <Visibility fontSize="small" />
                    ) : (
                      <VisibilityOff fontSize="small" />
                    )}
                    <Typography variant="body2">Use Vision</Typography>
                  </Box>
                </Tooltip>
              }
            />

            <Box sx={{ display: "flex", gap: 1 }}>
              <LoadingButton
                loading={executing}
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={handleExecuteTask}
                disabled={!task.trim() || !sessionId || cancelling}
                sx={{ flex: 1 }}
              >
                Execute Task
              </LoadingButton>
              {executing && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Cancel />}
                  onClick={handleCancelTask}
                  disabled={cancelling}
                >
                  {cancelling ? "Cancelling..." : "Cancel"}
                </Button>
              )}
            </Box>

            {result && (
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  {result.success ? "✓ Task Completed" : "⚠ Task Failed"}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {result.result}
                </Typography>
              </Paper>
            )}

            {history.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Execution History:
                </Typography>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    maxHeight: "300px",
                    overflowY: "auto",
                  }}
                >
                  {history.map((item, index) => (
                    <Typography
                      key={index}
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: 1,
                        fontFamily: "monospace",
                      }}
                    >
                      {index + 1}. {item}
                    </Typography>
                  ))}
                </Paper>
              </Box>
            )}
          </Box>
        </Box>

        {/* Right Panel - Live Browser (70%) */}
        <Box
          sx={{
            width: "70%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {!liveUrl ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                gap: 2,
                p: 4,
              }}
            >
              <Typography variant="h5" gutterBottom>
                Live Browser Session
              </Typography>
              <Typography variant="body1" color="text.secondary" align="center">
                {integration?.type === "linkedin_login"
                  ? "Open a live browser session to relogin to your LinkedIn account. This will update your saved session with fresh credentials."
                  : "Open a live browser session using this integration's saved session and settings. You can interact with websites and perform tasks manually."}
              </Typography>
              <LoadingButton
                loading={loading}
                variant="contained"
                onClick={handleOpenDebugSession}
                size="large"
              >
                {integration?.type === "linkedin_login"
                  ? "Open LinkedIn Login"
                  : "Open Live Browser"}
              </LoadingButton>
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  p: 1,
                  borderBottom: "1px solid #ddd",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Session ID: {sessionId}
                </Typography>
              </Box>
              <iframe
                src={/^https?:\/\/[a-zA-Z0-9]/.test(liveUrl) ? liveUrl : ""}
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                allow="clipboard-read; clipboard-write"
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
                title="Live Browser Session"
              />
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default BrowserSessionDialog;
